import { Controller } from "@hotwired/stimulus";

const EMPTY_STATES = {
  all: {
    title: "You have no notes or meeting transcripts available",
    body: "Add a note or meeting transcript to update your team.",
  },
  notes: {
    title: "You have no notes available",
    body: "Add a note to update your team.",
  },
  red_flags: {
    title: "You have no red flags available",
    body: "Add a red flag to capture a concern for your team.",
  },
  meeting_transcripts: {
    title: "You have no meeting transcripts available",
    body: "Add a meeting transcript so your team can revisit the conversation.",
  },
};

export default class extends Controller {
  static targets = [
    "activityList",
    "emptyState",
    "emptyStateBody",
    "emptyStateTitle",
    "filter",
    "noteForm",
    "transcriptForm",
  ];

  connect () {
    this.currentFilter = this.filterTarget.value || this.filterTarget.getAttribute("value") || "all";
    this.activityObserver = new MutationObserver(() => this.applyFilter());
    this.activityObserver.observe(this.activityListTarget, { childList: true });
    this.noteFormObserver = new MutationObserver(() => this.applyFilter());
    this.noteFormObserver.observe(this.noteFormTarget, { childList: true, subtree: true });
    this.applyFilter();
  }

  disconnect () {
    this.activityObserver.disconnect();
    this.noteFormObserver.disconnect();
  }

  filter (event) {
    this.currentFilter = event.currentTarget.value;
    this.applyFilter();
  }

  applyFilter () {
    const activities = Array.from(this.activityListTarget.querySelectorAll(":scope > [data-activity-kind]"));
    const visibleActivities = activities.filter(activity => this.visibleActivity(activity));

    activities.forEach(activity => {
      activity.classList.toggle("hidden", !this.visibleActivity(activity));
    });

    this.noteFormTarget.classList.toggle("hidden", this.currentFilter === "meeting_transcripts");
    this.transcriptFormTarget.classList.toggle("hidden", this.currentFilter !== "meeting_transcripts");
    this.updateRedFlagForm();
    this.renderEmptyState(visibleActivities.length === 0);
  }

  visibleActivity (activity) {
    if (this.currentFilter === "all") return true;
    if (this.currentFilter === "notes") return activity.dataset.activityKind === "note";
    if (this.currentFilter === "red_flags") return activity.dataset.redFlag === "true";

    return activity.dataset.activityKind === "meeting_transcript";
  }

  updateRedFlagForm () {
    const checkbox = this.noteFormTarget.querySelector("[data-red-flag-checkbox]");
    const lockInput = this.noteFormTarget.querySelector("[data-red-flag-lock-input]");
    const notice = this.noteFormTarget.querySelector("[data-red-flag-lock-notice]");

    if (!checkbox || !lockInput) return;

    const locked = this.currentFilter === "red_flags";

    if (locked && !checkbox.disabled) {
      checkbox.dataset.checkedBeforeRedFlagLock = checkbox.checked;
      checkbox.checked = true;
      checkbox.disabled = true;
    } else if (!locked && checkbox.disabled) {
      checkbox.disabled = false;
      checkbox.checked = checkbox.dataset.checkedBeforeRedFlagLock === "true";
      delete checkbox.dataset.checkedBeforeRedFlagLock;
    }

    lockInput.disabled = !locked;
    notice?.classList.toggle("hidden", !locked);
  }

  renderEmptyState (visible) {
    const copy = EMPTY_STATES[this.currentFilter];

    if (this.emptyStateTitleTarget.textContent !== copy.title) this.emptyStateTitleTarget.textContent = copy.title;
    if (this.emptyStateBodyTarget.textContent !== copy.body) this.emptyStateBodyTarget.textContent = copy.body;
    this.emptyStateTarget.classList.toggle("hidden", !visible);
  }
}
